import { useEffect, useRef, useState } from "react";
import { useLang } from "@/i18n";
import {
  getGaClientId, getAttributionContext, generateFormAttemptId,
  trackFormStarted, trackFormSubmitAttempted, trackFormSubmissionFailed,
} from "@/lib/analytics";
import { CHROME } from "./chrome";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FORM_ID = "playbook_request_form";
const ASSET_ID = "next_presentation_playbook";
const ASSET_VARIANT = "personalized";

/**
 * Presenter Playbook CTA (presenter-playbook-v1 addendum) — the result
 * page's one optional email offer, replacing the generic "save my
 * result" lead capture it used to show (no asking twice for the same
 * email). Only email is required; name is optional; marketing is a
 * separate opt-in defaulting to false. On success, downloads the
 * personalised six-page guide (server recomputes leaders from the 7
 * answers it re-sends — never trusts a client-supplied archetype).
 *
 * InterAct Tracking Foundation milestone (2026-10-09) — `form_started`
 * fires on the first real mutation of ANY of the 3 fields below,
 * carrying a persistent `form_attempt_id` for this whole form-filling
 * attempt; `handleSubmit` generates a FRESH `attempt_id` per click,
 * shared only between that click's `form_submit_attempted` and its
 * own `form_submission_failed` (never fired for the client-side-only
 * email-format rejection below — that never reaches `onRequestPlaybook`,
 * so it is not a dispatch attempt). `asset_variant: "personalized"`
 * and the Presenter Archetype's own `run_id` (passed down as the
 * `runId` prop — PRESERVED, never regenerated here) are included on
 * all 3 events so this entry point is distinguishable from the public
 * Guides page's generic one (`GuideRequestForm.jsx`, `asset_variant:
 * "generic"`, no `run_id`). `lead_capture_completed`/`lead_created`
 * are NOT emitted here — they are backend-owned, by
 * `routers/presenter_playbook_routes.py`, on an actually-accepted
 * request; a client-side validation rejection, a blocked-in-Preview
 * submission, or merely viewing/downloading the resulting PDF are
 * never counted as one.
 */
export const PlaybookCTA = ({ ui, onRequestPlaybook, runId }) => {
  const { lang } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [state, setState] = useState("idle"); // idle | requesting | saved | error
  const [fieldError, setFieldError] = useState(null);
  const [gaClientId, setGaClientId] = useState(null);
  const attribution = getAttributionContext() || {};
  const c = CHROME[lang] || CHROME.en;
  const formStartedRef = useRef(false);
  const formAttemptIdRef = useRef(null);

  useEffect(() => {
    getGaClientId().then(setGaClientId);
  }, []);

  const markFormStarted = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    formAttemptIdRef.current = generateFormAttemptId();
    trackFormStarted({ formId: FORM_ID, assetId: ASSET_ID, assetVariant: ASSET_VARIANT, formAttemptId: formAttemptIdRef.current, runId });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setFieldError(ui.emailInvalid);
      return;
    }
    setFieldError(null);
    setState("requesting");
    const attemptId = generateFormAttemptId();
    trackFormSubmitAttempted({ formId: FORM_ID, assetId: ASSET_ID, assetVariant: ASSET_VARIANT, formAttemptId: formAttemptIdRef.current, attemptId, runId });
    try {
      await onRequestPlaybook({ name: name.trim() || null, email: email.trim(), marketing, gaClientId });
      setState("saved");
    } catch (err) {
      const errorCode = !err?.status ? "network_error" : err.status >= 500 ? "server_error" : err.status === 429 ? "rate_limited" : "validation_error";
      trackFormSubmissionFailed({ formId: FORM_ID, assetId: ASSET_ID, assetVariant: ASSET_VARIANT, formAttemptId: formAttemptIdRef.current, attemptId, runId, errorCode });
      setState("error");
    }
  };

  if (state === "saved") {
    return (
      <div
        className="rounded-xl border border-[#607e66]/30 bg-[#607e66]/10 px-5 py-4 mt-8"
        data-testid="pp-cta-saved"
      >
        <p className="text-sm text-[#263b3d] font-medium">
          {c.playbookSuccess}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-black/10 bg-white px-5 py-6 mt-8" data-testid="pp-cta">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#b74e33] mb-1.5">{c.playbookEyebrow}</p>
      <h3 className="font-serif text-lg text-[#263b3d] mb-2">{c.playbookHeading}</h3>
      <p className="text-sm text-[#3f4a47] mb-4">{c.playbookBody}</p>

      <form onSubmit={handleSubmit} noValidate>
        <input type="hidden" name="ga_client_id" value={gaClientId || ""} data-testid="pp-cta-ga-client-id" />
        <input type="hidden" name="gclid" value={attribution.gclid || ""} data-testid="pp-cta-gclid" />
        <input type="hidden" name="oppref" value={attribution.oppref || ""} data-testid="pp-cta-oppref" />

        <input
          type="text"
          data-testid="pp-cta-name-input"
          value={name}
          onChange={(e) => { markFormStarted(); setName(e.target.value); }}
          placeholder={c.playbookNamePlaceholder}
          className="w-full rounded-lg border border-[#d9d5c6] px-4 py-3 text-sm text-[#263b3d] mb-3 focus:outline-none focus:ring-2 focus:ring-[#b74e33]"
        />

        <label htmlFor="pp-cta-email-input" className="block text-xs font-semibold uppercase tracking-wide text-[#6b6b63] mb-1.5">
          {c.playbookEmailLabel}
        </label>
        <input
          id="pp-cta-email-input"
          data-testid="pp-cta-email-input"
          type="email"
          value={email}
          onChange={(e) => { markFormStarted(); setEmail(e.target.value); }}
          placeholder={ui.emailPlaceholder}
          className="w-full rounded-lg border border-[#d9d5c6] px-4 py-3 text-sm text-[#263b3d] mb-3 focus:outline-none focus:ring-2 focus:ring-[#b74e33]"
        />

        <label className="flex items-start gap-2.5 text-sm text-[#3f4a47] mb-4 cursor-pointer">
          <input
            type="checkbox"
            data-testid="pp-cta-marketing-checkbox"
            checked={marketing}
            onChange={(e) => { markFormStarted(); setMarketing(e.target.checked); }}
            className="mt-0.5"
          />
          <span>{c.playbookMarketingLabel}</span>
        </label>

        {fieldError && (
          <p className="text-sm text-[#b74e33] mb-3" data-testid="pp-cta-field-error" role="alert">
            {fieldError}
          </p>
        )}
        {state === "error" && (
          <p className="text-sm text-[#b74e33] mb-3" data-testid="pp-cta-request-error" role="alert">
            {c.playbookError}
          </p>
        )}

        <button
          type="submit"
          data-testid="pp-cta-submit"
          disabled={state === "requesting"}
          className="w-full py-3 rounded-full font-semibold text-sm text-white bg-[#b74e33] hover:bg-[#9c3f29] disabled:opacity-50 transition-colors"
        >
          {state === "requesting" ? c.playbookPreparing : c.playbookButton}
        </button>

        <p className="text-[11px] text-[#9a968a] mt-3">{c.playbookRequestNotice}</p>
        <p className="text-[11px] text-[#9a968a] mt-1">{ui.privacy}</p>
      </form>
    </div>
  );
};

export default PlaybookCTA;
